import { useCallback, useEffect, useRef } from 'react';
import type { MouseEvent, PointerEvent, TouchEvent } from 'react';

type PressHandlers = {
  onPressStart: () => void;
  onPressEnd: () => void;
};

type InstantMainBetTapOptions = {
  enabled: boolean;
  isDisabled: boolean;
  onAction: () => void;
  /** When true, fires on pointer-down for mouse; touch waits for touchend. */
  fireOnPointerDown?: boolean;
};

type PointerSession = {
  fired: boolean;
};

type TouchGesture = {
  /** Touch.identifier for this finger, when known. */
  touchId: number | null;
  startClientY: number;
  /** True once vertical travel exceeds TOUCH_MOVE_THRESHOLD (a scroll, not a tap). */
  isScrollGesture: boolean;
};

/**
 * Vertical movement in px that distinguishes an intentional tap from a scroll
 * gesture. Used by Bet/Cashout touch handling — do not inline this value in
 * the event handlers.
 */
export const TOUCH_MOVE_THRESHOLD = 10;

/**
 * Handheld main bet: pointer-based tap handling so rapid taps on two bet panels
 * are not dropped when the first tap re-renders before a delayed click fires.
 * Supports simultaneous multi-touch (one session per pointer id).
 *
 * Touch also uses a movement threshold so scrolling over Bet/Cashout does not
 * fire the action. Mouse / desktop click behavior is unchanged.
 */
export function useInstantMainBetTap({
  enabled,
  isDisabled,
  onAction,
  fireOnPointerDown = false,
}: InstantMainBetTapOptions) {
  const disabledRef = useRef(isDisabled);
  const actionRef = useRef(onAction);
  const fireOnPointerDownRef = useRef(fireOnPointerDown);
  disabledRef.current = isDisabled;
  actionRef.current = onAction;
  fireOnPointerDownRef.current = fireOnPointerDown;

  const sessionsRef = useRef<Map<number, PointerSession>>(new Map());
  const suppressClickRef = useRef(false);
  const touchGestureRef = useRef<TouchGesture | null>(null);
  const detachWindowTouchMoveRef = useRef<(() => void) | null>(null);

  const tryFireAction = useCallback(() => {
    if (disabledRef.current) {
      return false;
    }
    actionRef.current();
    return true;
  }, []);

  const detachWindowTouchMove = useCallback(() => {
    detachWindowTouchMoveRef.current?.();
    detachWindowTouchMoveRef.current = null;
  }, []);

  const markScrollIfThresholdExceeded = useCallback((clientY: number) => {
    const gesture = touchGestureRef.current;
    if (!gesture || gesture.isScrollGesture) {
      return;
    }
    // Configurable threshold distinguishes an intentional tap from a scroll gesture.
    if (Math.abs(clientY - gesture.startClientY) > TOUCH_MOVE_THRESHOLD) {
      gesture.isScrollGesture = true;
    }
  }, []);

  const attachWindowTouchMove = useCallback(() => {
    if (detachWindowTouchMoveRef.current) {
      return;
    }
    const onWindowTouchMove = (event: globalThis.TouchEvent) => {
      const gesture = touchGestureRef.current;
      if (!gesture || gesture.isScrollGesture) {
        return;
      }
      let touch: Touch | undefined;
      for (let i = 0; i < event.touches.length; i++) {
        const candidate = event.touches[i];
        if (
          gesture.touchId == null ||
          candidate.identifier === gesture.touchId
        ) {
          touch = candidate;
          if (gesture.touchId != null) {
            break;
          }
        }
      }
      if (!touch) {
        return;
      }
      markScrollIfThresholdExceeded(touch.clientY);
    };
    window.addEventListener('touchmove', onWindowTouchMove, { passive: true });
    detachWindowTouchMoveRef.current = () => {
      window.removeEventListener('touchmove', onWindowTouchMove);
    };
  }, [markScrollIfThresholdExceeded]);

  const resetTouchGesture = useCallback(() => {
    touchGestureRef.current = null;
    detachWindowTouchMove();
  }, [detachWindowTouchMove]);

  useEffect(() => () => detachWindowTouchMove(), [detachWindowTouchMove]);

  const bindPress = useCallback(
    ({ onPressStart, onPressEnd }: PressHandlers) => {
      if (!enabled) {
        return {
          onMouseDown: onPressStart,
          onMouseUp: onPressEnd,
          onMouseLeave: onPressEnd,
          onTouchStart: onPressStart,
          onTouchMove: undefined,
          onTouchEnd: onPressEnd,
          onTouchCancel: onPressEnd,
          onPointerDown: undefined,
          onPointerMove: undefined,
          onPointerUp: undefined,
          onPointerCancel: undefined,
          onClick: () => {
            if (!disabledRef.current) {
              tryFireAction();
            }
            onPressEnd();
          },
        };
      }

      const beginTouchGesture = (touchId: number | null, clientY: number) => {
        touchGestureRef.current = {
          touchId,
          startClientY: clientY,
          isScrollGesture: false,
        };
        attachWindowTouchMove();
      };

      return {
        onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
          if (event.button !== 0) {
            return;
          }
          const session: PointerSession = { fired: false };
          sessionsRef.current.set(event.pointerId, session);
          onPressStart();

          const isTouch = event.pointerType === 'touch';
          if (isTouch) {
            // Defer the action until touchend so scroll can be distinguished from a tap.
            if (!touchGestureRef.current) {
              beginTouchGesture(null, event.clientY);
            }
            return;
          }

          if (fireOnPointerDownRef.current && !disabledRef.current) {
            session.fired = tryFireAction();
            if (session.fired) {
              suppressClickRef.current = true;
            }
          }
        },
        onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
          if (event.pointerType !== 'touch') {
            return;
          }
          markScrollIfThresholdExceeded(event.clientY);
        },
        onPointerUp: (event: PointerEvent<HTMLButtonElement>) => {
          if (event.button !== 0) {
            return;
          }
          const session = sessionsRef.current.get(event.pointerId);
          if (!session) {
            return;
          }
          onPressEnd();

          const isTouch = event.pointerType === 'touch';
          const isScrollGesture = Boolean(
            isTouch && touchGestureRef.current?.isScrollGesture
          );
          if (isTouch) {
            resetTouchGesture();
          }

          if (isScrollGesture) {
            // Finger moved past TOUCH_MOVE_THRESHOLD — treat as scrolling, not a tap.
            suppressClickRef.current = true;
            session.fired = true;
            sessionsRef.current.delete(event.pointerId);
            return;
          }

          if (!session.fired && !disabledRef.current) {
            session.fired = tryFireAction();
            if (session.fired) {
              suppressClickRef.current = true;
            }
          }
          sessionsRef.current.delete(event.pointerId);
        },
        onPointerCancel: (event: PointerEvent<HTMLButtonElement>) => {
          onPressEnd();
          resetTouchGesture();
          sessionsRef.current.delete(event.pointerId);
        },
        onTouchStart: (event: TouchEvent<HTMLButtonElement>) => {
          const touch = event.changedTouches[0];
          if (!touch) {
            return;
          }
          beginTouchGesture(touch.identifier, touch.clientY);
        },
        onTouchMove: (event: TouchEvent<HTMLButtonElement>) => {
          const gesture = touchGestureRef.current;
          if (!gesture || gesture.isScrollGesture) {
            return;
          }
          let clientY: number | undefined;
          for (let i = 0; i < event.changedTouches.length; i++) {
            const candidate = event.changedTouches[i];
            if (
              gesture.touchId == null ||
              candidate.identifier === gesture.touchId
            ) {
              clientY = candidate.clientY;
              break;
            }
          }
          if (clientY == null) {
            return;
          }
          markScrollIfThresholdExceeded(clientY);
        },
        onTouchEnd: () => {
          const gesture = touchGestureRef.current;
          const isScrollGesture = Boolean(gesture?.isScrollGesture);
          // pointerup usually runs first and already resolved the tap vs scroll.
          if (sessionsRef.current.size === 0) {
            return;
          }
          onPressEnd();
          resetTouchGesture();
          if (isScrollGesture) {
            suppressClickRef.current = true;
            sessionsRef.current.clear();
            return;
          }
          sessionsRef.current.forEach(session => {
            if (!session.fired && !disabledRef.current) {
              session.fired = tryFireAction();
              if (session.fired) {
                suppressClickRef.current = true;
              }
            }
          });
          sessionsRef.current.clear();
        },
        onTouchCancel: () => {
          onPressEnd();
          resetTouchGesture();
          sessionsRef.current.clear();
        },
        onMouseLeave: onPressEnd,
        onClick: (event: MouseEvent<HTMLButtonElement>) => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            event.preventDefault();
            onPressEnd();
            return;
          }
          if (!disabledRef.current) {
            tryFireAction();
          }
          onPressEnd();
        },
      };
    },
    [
      attachWindowTouchMove,
      enabled,
      markScrollIfThresholdExceeded,
      resetTouchGesture,
      tryFireAction,
    ]
  );

  return {
    bindPress,
    useInstantTap: enabled,
  };
}
